<script setup lang="ts">
import type { IncomingCallEvent } from '~/utils/tracking'

/** A card for every call ringing on a tracking number: who is calling, through which source, and their lead. */
const calls = ref<(IncomingCallEvent & { receivedAt: number })[]>([])
const toast = useToast()
const creating = ref<string | null>(null)
const CARD_LIFETIME = 90_000

const dismiss = (sessionId: string) => {
  calls.value = calls.value.filter((call) => call.session_id !== sessionId)
}

useTrackingEvents((event) => {
  if (event.event !== 'incoming_call') return
  dismiss(event.session_id)
  calls.value.unshift({ ...event, receivedAt: Date.now() })
  calls.value = calls.value.slice(0, 3)
  setTimeout(() => dismiss(event.session_id), CARD_LIFETIME)
})

const createLead = async (call: IncomingCallEvent) => {
  creating.value = call.session_id
  const { $api } = useNuxtApp()
  await $api<{ id: number; name: string }>('/tracking/inquiries/lead/', {
    method: 'POST',
    body: { phone: call.from },
  })
    .then((lead) => {
      call.lead = lead
      navigateTo(`/leads/${lead.id}`)
    })
    .catch((error) => processFormErrors(error, toast))
    .finally(() => (creating.value = null))
}
</script>

<template>
  <div
    class="bottom-4 fixed flex flex-col gap-3 right-4 w-80 z-[1100]"
    aria-live="polite"
  >
    <Card
      v-for="call in calls"
      :key="call.session_id"
      class="border-l-4 border-primary shadow-lg"
    >
      <template #content>
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <i class="animate-pulse pi pi-phone text-primary" />
            <span class="font-bold grow">Incoming call</span>
            <Button
              icon="pi pi-times"
              rounded
              size="small"
              variant="text"
              aria-label="Dismiss"
              @click="dismiss(call.session_id)"
            />
          </div>
          <div class="font-bold text-xl">
            {{ call.lead?.name ?? formatPhoneNumber(call.from) ?? call.from }}
          </div>
          <div v-if="call.lead" class="text-muted-color text-sm">
            {{ formatPhoneNumber(call.from) ?? call.from }}
          </div>
          <div class="text-sm">
            <span class="text-muted-color">Calling</span>
            <b>{{ call.source || call.tracking_label || call.to }}</b>
          </div>
          <div v-if="!call.is_new_customer && call.first_source" class="text-sm">
            <span class="text-muted-color">Returning customer · first came from</span>
            <b>{{ call.first_source }}</b>
          </div>
          <Tag v-else severity="success" value="New customer" />
          <div v-if="call.lead_status" class="flex gap-2 items-center text-sm">
            <span
              class="h-3 rounded w-3"
              :style="{ backgroundColor: `#${call.lead_status.color}` }"
            />
            {{ call.lead_status.name }}
          </div>
          <div class="flex gap-2 pt-1">
            <Button
              v-if="call.lead"
              icon="pi pi-user"
              label="Open lead"
              size="small"
              @click="navigateTo(`/leads/${call.lead.id}`)"
            />
            <Button
              v-else
              icon="pi pi-user-plus"
              label="Create lead"
              size="small"
              :loading="creating === call.session_id"
              @click="createLead(call)"
            />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
